import { isAbortError } from '@app/errors/AbortError'
import { AppError } from '@app/errors/AppError'

import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'

import type { ThunkAction } from '@app/store/thunk'

import type { PromiseState } from './promiseState'
import { createPromise } from './promiseState'
import type { LoadRequest } from './thunkSelector'

export type SliceState<T> = {
  hash: string
  expires_at: string
  data: T
}

export const withCheckedSliceState = <T>(
  c: { load: (load: ThunkAction<PromiseState>) => LoadRequest },
  {
    sliceState,
    slice,
    hash,
    date,
    load,
  }: {
    sliceState: SliceState<T> | undefined
    slice: { key: string; set: (state: SliceState<T>) => ThunkAction<unknown> }
    hash?: string
    date?: { date: Moment; expiresInMinutes?: number }
    load: ThunkAction<Promise<T>>
  }
) => {
  const expired = (() => {
    if (!sliceState) return true
    if (date) {
      if (!sliceState.expires_at) throw new Error(`${slice.key}: sliceState.expires_at is missing`)
      const expiresAt = moment(sliceState.expires_at)
      if (expiresAt.isBefore(date.date)) return true
    }
    if (typeof hash === 'string') {
      return sliceState.hash !== hash
    }
    return false
  })()
  if (expired) {
    const loadHash = [hash ?? '', date ? date.date.format() : ''].join(':')
    return c.load(
      createPromise(`Load ${slice.key}`, loadHash, async dispatch => {
        const data = await dispatch(load).catch(namedAfterTheSlice(slice.key))
        dispatch(
          slice.set({
            hash: hash ?? '',
            expires_at: date ? date.date.clone().add(date.expiresInMinutes, 'minutes').format() : '',
            data,
          })
        )
      })
    )
  }
  if (!sliceState) throw new Error(`${slice.key}: sliceState is undefined`)
  return sliceState.data
}

/**
 * Names the loader that broke. A failed load reaches Sentry as whatever the api threw — an endpoint
 * and a status, with nothing saying which selector asked or why. Wrapping here gives the report a
 * chain (`Load account_issues failed <- 401 - GET …`), and groups every failure of one loader as
 * one issue instead of one per status and error class.
 *
 * An abort is passed through untouched: it is recognised by its own type, and a wrapper hides that.
 */
const namedAfterTheSlice =
  (key: string) =>
  (e: unknown): never => {
    if (isAbortError(e)) throw e
    throw new AppError(`Load ${key} failed`).withCause(e)
  }
